import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { supabase } from '../lib/supabase';
import SEOHead from './SEOHead';
import Header from './Header';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';
import NewsletterSignup from './NewsletterSignup';
import { getBreadcrumbStructuredData } from '../utils/structuredData';
import { Clock, ArrowLeft, ArrowRight, Loader2, ChevronRight } from 'lucide-react';

interface CategoryPost {
  id: string;
  slug: string;
  title_es: string;
  title_en: string;
  excerpt_es: string;
  excerpt_en: string;
  featured_image: string | null;
  author_name: string;
  author_avatar: string | null;
  reading_time_minutes: number;
  published_at: string;
}

interface CategoryInfo {
  id: string;
  slug: string;
  name_es: string;
  name_en: string;
  description_es: string | null;
  description_en: string | null;
}

const BlogCategory: React.FC = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const { language } = useLanguage();
  const t = translations[language].blog;

  const [category, setCategory] = useState<CategoryInfo | null>(null);
  const [posts, setPosts] = useState<CategoryPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!categorySlug) return;

    const fetchData = async () => {
      setLoading(true);
      setNotFound(false);

      const { data: catData, error: catError } = await supabase
        .from('blog_categories')
        .select('id, slug, name_es, name_en, description_es, description_en')
        .eq('slug', categorySlug)
        .maybeSingle();

      if (catError || !catData) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setCategory(catData);

      const { data: postsData } = await supabase
        .from('blog_posts')
        .select('id, slug, title_es, title_en, excerpt_es, excerpt_en, featured_image, author_name, author_avatar, reading_time_minutes, published_at')
        .eq('is_published', true)
        .eq('category_id', catData.id)
        .order('published_at', { ascending: false });

      if (postsData) setPosts(postsData);
      setLoading(false);
    };

    fetchData();
  }, [categorySlug]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const categoryName = category ? (language === 'es' ? category.name_es : category.name_en) : '';
  const categoryDesc = category ? (language === 'es' ? category.description_es : category.description_en) : '';

  const breadcrumb = category ? getBreadcrumbStructuredData([
    { name: 'Home', url: 'https://laimu.mx' },
    { name: 'Blog', url: 'https://laimu.mx/blog' },
    { name: categoryName, url: `https://laimu.mx/blog/category/${category.slug}` }
  ]) : null;

  if (loading) {
    return (
      <>
        <Header />
        <ScrollToTop />
        <div className="flex justify-center items-center py-32">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#115d7b' }} />
        </div>
        <Footer />
      </>
    );
  }

  if (notFound || !category) {
    return (
      <>
        <Header />
        <ScrollToTop />
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600 mb-8">{language === 'es' ? 'Categoría no encontrada.' : 'Category not found.'}</p>
          <a href="/blog" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold" style={{ backgroundColor: '#115d7b' }}>
            <ArrowLeft className="w-4 h-4" />
            {t.backToBlog}
          </a>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${categoryName} | LAIMU Blog`}
        description={categoryDesc || undefined}
        url={`/blog/category/${category.slug}`}
        type="blog"
        structuredData={breadcrumb}
      />
      <Header />
      <ScrollToTop />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 py-3">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <a href="/" className="hover:text-teal-600 transition-colors">Home</a>
            <ChevronRight className="w-3.5 h-3.5" />
            <a href="/blog" className="hover:text-teal-600 transition-colors">Blog</a>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-gray-700 font-medium">{categoryName}</span>
          </nav>
        </div>
      </div>

      {/* Category Header */}
      <section className="pt-12 pb-8" style={{ background: 'linear-gradient(135deg, #115d7b 0%, #0d4a63 100%)' }}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold text-white mb-3">{categoryName}</h1>
            {categoryDesc && <p className="text-lg text-white/80">{categoryDesc}</p>}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6">
          {posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">{t.noResults}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map(post => (
                <a
                  key={post.id}
                  href={`/blog/${post.slug}`}
                  className="group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-white border border-gray-100 hover:-translate-y-1"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.featured_image || 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1'}
                      alt={language === 'es' ? post.title_es : post.title_en}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: '#ff8c00' }}>
                        {categoryName}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                      {language === 'es' ? post.title_es : post.title_en}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {language === 'es' ? post.excerpt_es : post.excerpt_en}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img
                          src={post.author_avatar || `https://ui-avatars.com/api/?name=${post.author_name}&background=115d7b&color=fff&size=32`}
                          alt={post.author_name}
                          className="w-7 h-7 rounded-full object-cover"
                        />
                        <span className="text-xs text-gray-500">{post.author_name}</span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-400 text-xs">
                        <Clock className="w-3 h-3" />
                        {post.reading_time_minutes} {t.minutesRead}
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <NewsletterSignup variant="card" />
        </div>
      </section>

      {/* Back to Blog */}
      <div className="pb-12 text-center">
        <a href="/blog" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold border-2 transition-all duration-300 hover:shadow-lg" style={{ borderColor: '#115d7b', color: '#115d7b' }}>
          <ArrowLeft className="w-4 h-4" />
          {t.backToBlog}
        </a>
      </div>

      <Footer />
    </>
  );
};

export default BlogCategory;
